import { createSelector } from '@ngrx/store';
import { StockStatus } from '../core/models';
import { stockItemStatus } from '../core/utils/stock.util';
import { selectFilters } from './catalog/catalog.selectors';
import { selectLowStockThreshold } from './profile/profile.selectors';
import { selectStockItems } from './stock/stock.selectors';

export interface InventoryRow {
  productId: string;
  name: string;
  skuCode: string;
  price: number;
  quantity: number;
  status: StockStatus;
}

/** Every shelf-stock item paired with its computed stock status. */
export const selectInventoryRows = createSelector(
  selectStockItems,
  selectLowStockThreshold,
  (items, low): InventoryRow[] =>
    items.map((item) => ({
      productId: item.productId,
      name: item.itemName,
      // The stock endpoint doesn't return a separate SKU code — the product id is the SKU.
      skuCode: item.productId,
      price: item.price,
      quantity: item.quantity,
      status: stockItemStatus(item, low),
    })),
);

/** Inventory rows narrowed by the shared header search box (name or SKU code). */
export const selectFilteredInventoryRows = createSelector(
  selectInventoryRows,
  selectFilters,
  (rows, f): InventoryRow[] => {
    const q = f.query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.skuCode.toLowerCase().includes(q),
    );
  },
);

export interface InventoryStats {
  ok: number;
  low: number;
  out: number;
  total: number;
}

export const selectInventoryStats = createSelector(
  selectInventoryRows,
  (rows): InventoryStats => ({
    ok: rows.filter((r) => r.status === 'ok').length,
    low: rows.filter((r) => r.status === 'low').length,
    out: rows.filter((r) => r.status === 'out').length,
    total: rows.length,
  }),
);
